import {parseIndicatorSource} from './indicator-sources.mjs'
// Each calculation timeframe owns a worker. Native/chart-timeframe studies keep
// the original worker and never share their series with resampled calculations.
export const EXECUTION_TIMEFRAMES = ['Chart','1m','3m','5m','15m','30m','1h','2h','4h','1D','1W','1M'];
export function timeframeMs(value) {
    if (typeof value === 'number') return value;
    const match=/^(\d+)([mhdwMDW])$/.exec(value||'');
    return match ? Number(match[1])*({m:60000,h:3600000,d:86400000,D:86400000,w:604800000,W:604800000,M:2592000000}[match[2]]) : NaN;
}
export function bucketStart(time, timeframe) {
    if (timeframe==='1M') { const date=new Date(time); return Date.UTC(date.getUTCFullYear(),date.getUTCMonth(),1); }
    const ms=timeframeMs(timeframe),offset=timeframe==='1W'?3*86400000:0;
    return Math.floor((time+offset)/ms)*ms-offset;
}
export function bucketEnd(time, timeframe) {
    if(timeframe==='1M'){const date=new Date(time);return Date.UTC(date.getUTCFullYear(),date.getUTCMonth()+1,1);}
    return bucketStart(time,timeframe)+timeframeMs(timeframe);
}
export function aggregateCandles(rows,timeframe) {
    const result=[];
    for(const row of rows){
        const time=bucketStart(row[0],timeframe),last=result.at(-1);
        if(last?.[0]===time){last[2]=Math.max(last[2],row[2]);last[3]=Math.min(last[3],row[3]);last[4]=row[4];last[5]+=row[5];}
        else result.push([time,...row.slice(1,6)]);
    }
    return result;
}
// Historical confirmed values become available on the last constituent bar.
// The live bar stays unconfirmed until its actual interval has completed.
export function projectIndicator(rows,candles,timeframe,chartMs,waitForClose=true) {
    let index=0,last=null;const width=Math.max(1,(rows[0]?.length||2)-1);
    return candles.map((bar,i)=>{
        const cutoff=bar[0]+(i<candles.length-1?chartMs:0);
        while(index<rows.length && bucketEnd(rows[index][0],timeframe)<=cutoff)last=rows[index++];
        if(!waitForClose&&i===candles.length-1&&rows[index]?.[0]<=bar[0])last=rows[index];
        return [bar[0],...(last?last.slice(1):Array(width).fill(NaN))];
    });
}

export class IndicatorExecution {
    constructor({createWorker,sendMain,emit,readBars}) {
        Object.assign(this,{createWorker,sendMain,emit,readBars});
        this.scripts=new Map();this.retired=new Set();this.groups=new Map();this.chartMs=60000;this.range=[];this.raw=[];
    }
    destroy(){for(const group of this.groups.values())group.worker.terminate();this.groups.clear();this.scripts.clear();}
    target(script){
        if(!script.src?.conf?.resample)return 'main';
        const selected=script.sett?.executionTimeframe;
        if(!selected||selected==='Chart'||timeframeMs(selected)===this.chartMs)return 'main';
        return selected;
    }
    post(target,message){target==='main'?this.sendMain(message):this.group(target).worker.postMessage(message);}
    group(target){
        if(this.groups.has(target))return this.groups.get(target);
        const group={worker:this.createWorker(),outputs:new Map(),running:false};this.groups.set(target,group);
        group.worker.onmessage=event=>this.receive(target,event.data);
        group.worker.onerror=()=>{group.running=false;this.emit({type:'engine-state',data:{scripts:this.scripts.size,running:false}});};
        if(this.settings)group.worker.postMessage({type:'update-dc-settings',data:this.settings});
        group.worker.postMessage({type:'send-meta-info',data:{tf:timeframeMs(target),range:this.range}});
        this.upload(target);return group;
    }
    bars(){return this.raw.length?this.raw:this.readBars();}
    upload(target){
        const group=this.groups.get(target);if(!group)return;
        const rows=aggregateCandles(this.bars(),target);group.lastTime=rows.at(-1)?.[0];
        group.worker.postMessage({type:'upload-data',data:{ohlcv:rows}});
    }
    register(script,tf=this.chartMs,range=this.range){
        this.chartMs=timeframeMs(tf)||this.chartMs;this.range=range;
        this.retired.delete(script.uuid);
        const old=this.scripts.get(script.uuid),target=this.target(script);
        if(old&&!old.invalid&&old.target!==target){this.post(old.target,{type:'remove-scripts',data:[script.uuid]});this.groups.get(old.target)?.outputs.delete(script.uuid);}
        const record={script,target};this.scripts.set(script.uuid,record);
        const dependency=parseIndicatorSource(script.sett?.source);
        const parent=dependency&&this.scripts.get(dependency.id);
        const sourceError=parent&&parent.target!==target?'Source and target indicators must use the same calculation timeframe.':null;
        // Data below the chart interval cannot be reconstructed from OHLCV bars.
        if(sourceError||(target!=='main'&&timeframeMs(target)<this.chartMs)){
            record.invalid=true;
            if(old&&!old.invalid)this.post(old.target,{type:'remove-scripts',data:[script.uuid]});
            this.emit({type:'modify-overlay',data:{uuid:script.uuid,fields:{executionError:sourceError||'Choose a calculation timeframe at least as large as the chart interval.'}}});
            this.emit({type:'overlay-data',data:[{id:script.uuid,data:[],new_ovs:{onchart:[],offchart:[]}}]});
            return;
        }
        this.emit({type:'modify-overlay',data:{uuid:script.uuid,fields:{executionError:null}}});
        this.post(target,{type:'exec-script',data:{s:script,tf:target==='main'?this.chartMs:timeframeMs(target),range}});
    }
    prune(){
        for(const [target,group] of this.groups)if(![...this.scripts.values()].some(record=>record.target===target&&!record.invalid)){
            group.worker.terminate();this.groups.delete(target);
        }
    }
    send(message){
        const {type,data}=message;
        if(type==='exec-script'){this.register(data.s,data.tf,data.range);this.prune();return true;}
        if(type==='update-ov-settings'){
            const delta={};
            for(const [id,settings] of Object.entries(data.delta)){
                const record=this.scripts.get(id);if(!record){delta[id]=settings;continue;}
                const script={...record.script,src:{...record.script.src,props:{...record.script.src.props}},sett:settings};
                for(const key of Object.keys(script.src.props))script.src.props[key]={...script.src.props[key],val:settings[key]??script.src.props[key].def};
                if(record.invalid||record.script.sett?.source!==settings.source||record.target!=='main'||this.target(script)!=='main'){this.register(script,data.tf,data.range);}
                else{record.script=script;delta[id]=settings;}
            }
            if(Object.keys(delta).length)this.sendMain({...message,data:{...data,delta}});
            for(const record of [...this.scripts.values()]){const dependency=parseIndicatorSource(record.script.sett?.source),parent=dependency&&this.scripts.get(dependency.id);if(parent&&(record.invalid||parent.target!==record.target))this.register(record.script,data.tf,data.range);}
            this.prune();return true;
        }
        if(type==='remove-scripts'){
            for(const id of data){const record=this.scripts.get(id);if(record){if(!record.invalid)this.post(record.target,{type,data:[id]});this.groups.get(record.target)?.outputs.delete(id);this.scripts.delete(id);this.retired.add(id);if(this.retired.size>4096)this.retired.delete(this.retired.values().next().value);}}
            for(const record of this.scripts.values())if(data.includes(parseIndicatorSource(record.script.sett?.source)?.id)){
                this.emit({type:'modify-overlay',data:{uuid:record.script.uuid,fields:{executionError:'The source indicator was removed. Select another source.'}}});
                this.emit({type:'overlay-data',data:[{id:record.script.uuid,data:[],new_ovs:{onchart:[],offchart:[]}}]});
            }
            this.prune();return false;
        }
        if(type==='send-meta-info'||type==='exec-all-scripts'){
            const previous=this.chartMs;this.chartMs=timeframeMs(data.tf)||this.chartMs;this.range=data.range;
            if(previous!==this.chartMs)for(const record of [...this.scripts.values()])this.register(record.script,this.chartMs,data.range);
            this.prune();
            for(const [target] of this.groups)this.post(target,{...message,data:{...data,tf:timeframeMs(target)}});
        }
        if(type==='upload-data'&&data.ohlcv){this.raw=data.ohlcv.map(row=>row.slice());for(const target of this.groups.keys())this.upload(target);}
        if(type==='update-data'&&data.ohlcv){
            if(!this.raw.length)this.raw=this.readBars().map(row=>row.slice());
            for(const row of data.ohlcv){const last=this.raw.at(-1);if(!last||row[0]>last[0])this.raw.push(row.slice());else if(row[0]===last[0])this.raw[this.raw.length-1]=row.slice();}
            for(const [target,group] of this.groups){
                let from=this.raw.length;
                while(from>0&&bucketStart(this.raw[from-1][0],target)>=group.lastTime)from--;
                const changed=aggregateCandles(this.raw.slice(from),target);
                group.lastTime=changed.at(-1)?.[0]??group.lastTime;
                if(changed.length)this.post(target,{type:'update-data',data:{ohlcv:changed}});
            }
        }
        if(type==='update-dc-settings'){this.settings=data;for(const target of this.groups.keys())this.post(target,message);}
        return false;
    }
    receive(target,message){
        const {type,data}=message;
        if(target!=='main'&&(type==='request-data'||type==='data-uploaded')){if(type==='request-data')this.upload(target);return;}
        if(type==='engine-state'){
            const group=this.groups.get(target);if(group)group.running=data.running;
            this.emit({...message,data:{...data,scripts:this.scripts.size}});return;
        }
        if(type==='overlay-data'||type==='overlay-update'){
            const items=[];
            for(const item of data){
                if(this.retired.has(item.id))continue;
                const record=this.scripts.get(item.id);
                if(target==='main'&&!record){items.push(item);continue;}
                if(!record||record.target!==target||record.invalid)continue;
                if(target==='main'){items.push(item);continue;}
                const group=this.groups.get(target);if(!group)continue;
                let rows=type==='overlay-data'?item.data:(group.outputs.get(item.id)||[]);
                if(type==='overlay-update'&&item.data){rows=rows.slice();if(rows.at(-1)?.[0]===item.data[0])rows[rows.length-1]=item.data;else rows.push(item.data);}
                if(!rows){items.push(item);continue;}
                group.outputs.set(item.id,rows);
                items.push({...item,data:projectIndicator(rows,this.bars(),target,this.chartMs,record.script.sett.waitForClose!==false),new_ovs:{onchart:[],offchart:[]}});
            }
            if(items.length)this.emit({type:target==='main'?type:'overlay-data',data:items});
            return;
        }
        this.emit(message);
    }
}
